import { useNavigate, useLocation } from "react-router-dom";
import { getUserListDetails } from "../../../../Service/user";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { CommonImages } from "../../../../reusableUI/images";
import Loader from "../../../../reusableUI/loader";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { SiTicktick } from "react-icons/si";
import ReusableTable from "../../../../reusableUI/table";
import Page from "../../../../reusableUI/page";
import {
  formatPhoneNumber,
  capitalizeFirstLetter,
} from "../../../../utils/Functions";
import moment from "moment";
import { VscEye } from "react-icons/vsc";
import { MdCancel } from "react-icons/md";
import CommonDropdown from "../../../../reusableUI/dropdown";
import {
  MdOutlineFilterList,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from "react-icons/md";
import { GrPowerReset } from "react-icons/gr";
import { FaUserGroup } from "react-icons/fa6";
import { HiOutlineDownload } from "react-icons/hi";
import {
  Modal,
  Select,
  Upload,
  Button,
  message,
  Space,
  Typography,
} from "antd";
import Papa from "papaparse";
import { CiHospital1 } from "react-icons/ci";
import "react-toastify/dist/ReactToastify.css";

const { Option } = Select;
const { Dragger } = Upload;
const { Title, Text } = Typography;

const User = ({ searchTerm }) => {
  const [loading, setLoading] = useState(true);
  const [paginationPage, setPaginationPage] = useState(0);
  const [selectedValue, setSelectedValue] = useState("all");
  const [userList, setUserList] = useState([]);
  const [noUsersFound, setNoUsersFound] = useState(false);
  const [showResetPopup, setShowResetPopup] = useState(false);
  const [selectedPageCount, setSelectedPageCount] = useState(() => {
    const savedPageCount = localStorage.getItem("pageCount");
    return savedPageCount ? parseInt(savedPageCount, 10) : 50;
  });

  const [sortBy, setSortBy] = useState(() => {
    const savedSortBy = localStorage.getItem("sortBy");
    return savedSortBy ? JSON.parse(savedSortBy) : [];
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);

  // -------------------- FIX: default filters + merge saved filters (adds partnerName safely) --------------------
  const defaultFilters = useMemo(
    () => ({
      role: [],
      userType: [],
      profile: [],
      registeredOn: [],
      payment: [],
      aadhar: [],
      certificate: [],
      license: [],
      location: [],
      onboardedType: [],
      partnerName: [], // NEW
    }),
    []
  );

  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem("filters");
    if (!saved) return defaultFilters;
    try {
      const parsed = JSON.parse(saved);
      return { ...defaultFilters, ...parsed };
    } catch {
      return defaultFilters;
    }
  });

  const [isFilterApplied, setIsFilterApplied] = useState(false);

  useEffect(() => {
    const applied = Object.values(filters).some(
      (v) => Array.isArray(v) && v.length > 0
    );
    setIsFilterApplied(applied);
  }, [filters]);

  const location = useLocation();
  const { verification, fromDashboard } = location.state || {};
  const navigate = useNavigate();

  useEffect(() => {
    getUserList();
    const savedValue = localStorage.getItem("selectedValue");
    if (savedValue) {
      setSelectedValue(savedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (verification === "pending" && fromDashboard) {
      setTimeout(() => {
        window.history.replaceState({}, document.title);
        setIsFilterApplied(true);
      }, 100);
    }
  }, [verification, fromDashboard]);

  useEffect(() => {
    localStorage.setItem("filters", JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    if (location.state?.currentPage !== undefined) {
      setPaginationPage(location.state.currentPage);
    }
  }, [location.state]);

  useEffect(() => {
    if (searchTerm) {
      setPaginationPage(0);
    }
  }, [searchTerm]);

  const getUserList = async () => {
    const result = await getUserListDetails();
    if (!result.error) {
      setLoading(false);
      setUserList(result.data);
    } else {
      setLoading(false);
      console.log("Error fetching");
    }
  };

  const pageCount = [
    { label: "25", value: 25 },
    { label: "50", value: 50 },
    { label: "75", value: 75 },
    { label: "100", value: 100 },
  ];

  const handleChangePageCount = (event) => {
    const newValue = event.target.value;
    setSelectedPageCount(newValue);
    localStorage.setItem("pageCount", newValue);
    window.location.reload();
  };

  const navigateToProfile = (index, original) => {
    localStorage.setItem("profilePage", "user");
    navigate(`/profile/${original.index || original._id}-${original._id}`, {
      state: {
        userId: original._id,
        from: window.location.pathname,
        currentPage: paginationPage,
        page: "user",
      },
    });
  };

  const normalizeLabel = (val) =>
    capitalizeFirstLetter(
      String(val || "")
        .trim()
        .toLowerCase()
    );

  // -------------------- NEW: partner name normalization + options --------------------
  const normalizePartnerName = (val) => String(val || "").trim().toLowerCase();

  const partnerNameOptions = useMemo(() => {
    const map = new Map(); // key -> { value, label, count }

    (userList || []).forEach((u) => {
      const pType = u?.onboardedBy?.partnerType;
      const pName = u?.onboardedBy?.partnerName;

      if (!pType || pType === "self") return;
      if (!pName || !String(pName).trim()) return;

      const key = normalizePartnerName(pName);

      if (!map.has(key)) {
        map.set(key, { value: key, label: String(pName).trim(), count: 0 });
      }
      map.get(key).count += 1;
    });

    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [userList]);

  const togglePartnerName = (value) => {
    setFilters((prev) => {
      const current = prev.partnerName || [];
      const exists = current.includes(value);
      return {
        ...prev,
        partnerName: exists
          ? current.filter((v) => v !== value)
          : [...current, value],
      };
    });
  };

  const locationMap = useMemo(() => {
    const map = new Map();
    userList.forEach((item) => {
      const dRaw = item?.personalInfo?.addressInfo?.district;
      if (!dRaw) return;
      const cRaw =
        item?.personalInfo?.addressInfo?.city ||
        item?.personalInfo?.addressInfo?.area ||
        "";
      const district = normalizeLabel(dRaw);
      const city = cRaw ? normalizeLabel(cRaw) : "";

      if (!map.has(district)) map.set(district, new Set());
      if (city) map.get(district).add(city);
    });

    const obj = {};
    Array.from(map.keys())
      .sort((a, b) => a.localeCompare(b))
      .forEach((d) => {
        obj[d] = Array.from(map.get(d)).sort((a, b) => a.localeCompare(b));
      });
    return obj;
  }, [userList]);

  const getDistrictCount = (district) => {
    return userList.filter(
      (item) =>
        normalizeLabel(item?.personalInfo?.addressInfo?.district) === district
    ).length;
  };

  const getCityCount = (district, city) => {
    return userList.filter((item) => {
      const d = normalizeLabel(item?.personalInfo?.addressInfo?.district);
      const c = normalizeLabel(
        item?.personalInfo?.addressInfo?.city ||
        item?.personalInfo?.addressInfo?.area
      );
      return d === district && c === city;
    }).length;
  };

  const [expandedDistricts, setExpandedDistricts] = useState({});
  const toggleDistrictExpand = (district) =>
    setExpandedDistricts((prev) => ({ ...prev, [district]: !prev[district] }));

  const toggleLocationDistrict = (district) => {
    setFilters((prev) => {
      const current = new Set(prev.location || []);
      const allCities = locationMap[district] || [];

      if (current.has(district)) {
        current.delete(district);
        allCities.forEach((c) => current.delete(`${district}::${c}`));
      } else {
        allCities.forEach((c) => current.delete(`${district}::${c}`));
        current.add(district);
      }
      return { ...prev, location: Array.from(current) };
    });
  };

  const toggleLocationCity = (district, city) => {
    setFilters((prev) => {
      const current = new Set(prev.location || []);
      const token = `${district}::${city}`;
      const allCities = locationMap[district] || [];

      if (current.has(token)) {
        current.delete(token);
      } else {
        current.delete(district);
        current.add(token);
      }

      const selectedCount = allCities.filter((c) =>
        current.has(`${district}::${c}`)
      ).length;
      if (allCities.length > 0 && selectedCount === allCities.length) {
        allCities.forEach((c) => current.delete(`${district}::${c}`));
        current.add(district);
      }

      return { ...prev, location: Array.from(current) };
    });
  };

  const getExpectedCompletion = (role, subRole) => {
    if (role === "patient") return "5of5";
    if (role === "nurse") {
      if (subRole === "nursing-assistant") return "7of7";
      return "7of7";
    }
    if (role === "physiotherapist") return "7of7";
    return "7of7";
  };

  const isProfileCompleted = (item) => {
    const rawValue = item?.profileCompletion || "";
    const normalized = rawValue.toString().replace(/\s/g, "").toLowerCase();

    const isHospitalOnboarded =
      item?.onboardedBy?.partnerType &&
      item.onboardedBy.partnerType !== "self";

    if (isHospitalOnboarded) {
      // For hospital-onboarded users, 1 of 1 = completed
      return normalized === "1of1";
    }

    const expected = getExpectedCompletion(item?.role, item?.subRole);
    return normalized === expected.toLowerCase();
  };

  const getColumns = () => {
    let baseColumns = [
      {
        Header: "Name",
        accessor: "name",
        id: "name",
        disableSortBy: false,
        sortType: (rowA, rowB) => {
          const isPatientA = rowA.original?.role === "patient";
          const isPatientB = rowB.original?.role === "patient";

          const piNameA = rowA.original?.personalInfo?.basicInfo?.nameInfo;
          const cpNameA =
            rowA.original?.contactPersonInfo?.basicInfo?.nameInfo;
          const piNameB = rowB.original?.personalInfo?.basicInfo?.nameInfo;
          const cpNameB =
            rowB.original?.contactPersonInfo?.basicInfo?.nameInfo;

          const fullNameA =
            (isPatientA
              ? piNameA?.fullName || cpNameA?.fullName
              : piNameA?.fullName) || "";
          const fullNameB =
            (isPatientB
              ? piNameB?.fullName || cpNameB?.fullName
              : piNameB?.fullName) || "";

          return fullNameA.toLowerCase().localeCompare(fullNameB.toLowerCase());
        },
        headerStyle: { textAlign: "left", padding: "8px" },
        style: {
          textAlign: "left",
          padding: "8px",
          whiteSpace: "nowrap",
          overflow: "visible",
        },
        Cell: ({ row, row: { original } }) => {
          const isPatient = original?.role === "patient";
          const piName = original?.personalInfo?.basicInfo?.nameInfo;
          const cpName = original?.contactPersonInfo?.basicInfo?.nameInfo;

          const title =
            (isPatient ? piName?.title || cpName?.title : piName?.title) ||
            "N/A";
          const fullName =
            (isPatient
              ? piName?.fullName || cpName?.fullName
              : piName?.fullName) || "N/A";

          const gender = original?.personalInfo?.basicInfo?.otherInfo?.gender;

          const profileSrc =
            original?.profilePicture ||
            (gender === "male"
              ? isPatient
                ? CommonImages.menwithoutactive
                : original?.active
                  ? CommonImages.menwithactive
                  : CommonImages.menwithoutactive
              : gender === "female"
                ? isPatient
                  ? CommonImages.womenwithoutactive
                  : original?.active
                    ? CommonImages.womenwithactive
                    : CommonImages.womenwithoutactive
                : isPatient
                  ? CommonImages.commonwithoutactive
                  : original?.active
                    ? CommonImages.commonactive
                    : CommonImages.commonwithoutactive);

          const hasContactPerson = Boolean(
            cpName && (cpName?.fullName || cpName?.title)
          );

          return (
            <div className="flex flex-row items-center gap-1.5 whitespace-nowrap ml-2">
              <div className="relative inline-block shrink-0">
                <img
                  src={profileSrc}
                  alt="Profile"
                  className="h-8 w-8 object-cover"
                />
              </div>

              <span
                className="text-blue-600 text-[12px] font-normal"
                title={`${title}. ${fullName}`}
              >
                <p className="max-w-[130px] truncate">
                  {title}. {fullName}
                </p>
              </span>

              {original?.onboardedBy?.partnerType !== "self" && (
                <CiHospital1 size={14} color="blue" className="shrink-0" />
              )}

              {original?.role === "patient" && hasContactPerson && !piName && (
                <FaUserGroup
                  size={10}
                  color="#b042f5"
                  className="shrink-0"
                  title="Account Holder"
                />
              )}
            </div>
          );
        },
      },

      {
        Header: "Role",
        accessor: "role",
        id: "role",
        width: 110,
        minWidth: 100,
        maxWidth: 120,
        disableSortBy: false,
        sortType: (rowA, rowB) => {
          let roleA = "";
          let roleB = "";

          if (rowA.original.role === "physiotherapist") roleA = "Physio";
          else if (rowA.original.role === "patient") roleA = "Patient";
          else if (rowA.original.subRole === "nursing-assistant")
            roleA = "Nur-Assist";
          else if (rowA.original.subRole && rowA.original.role === "nurse")
            roleA = rowA.original.subRole;
          else roleA = rowA.original.role || "";

          if (rowB.original.role === "physiotherapist") roleB = "Physio";
          else if (rowB.original.role === "patient") roleB = "Patient";
          else if (rowB.original.subRole === "nursing-assistant")
            roleB = "Nur-Assist";
          else if (rowB.original.subRole && rowB.original.role === "nurse")
            roleB = rowB.original.subRole;
          else roleB = rowB.original.role || "";

          return roleA.toLowerCase().localeCompare(roleB.toLowerCase());
        },
        headerStyle: { textAlign: "center", padding: "8px" },
        style: { textAlign: "center", padding: "8px" },
        Cell: ({ row: { original } }) => {
          let roleDisplay = "";

          if (original.role === "physiotherapist") roleDisplay = "Physio";
          else if (original.role === "patient") roleDisplay = "Patient";
          else if (original.subRole === "nursing-assistant")
            roleDisplay = "Nur-Assist";
          else if (original.subRole && original.role === "nurse")
            roleDisplay = capitalizeFirstLetter(original.subRole);
          else roleDisplay = capitalizeFirstLetter(original.role);

          return (
            <span className="text-[12px]">
              <p className="w-40">{roleDisplay}</p>
            </span>
          );
        },
      },

      {
        Header: "Contact",
        accessor: "phone",
        id: "phone",
        width: 130,
        minWidth: 120,
        maxWidth: 140,
        disableSortBy: false,
        sortType: "alphanumeric",
        headerStyle: { textAlign: "center", padding: "8px" },
        style: { textAlign: "center", padding: "8px" },
        Cell: ({ value }) => (
          <span className="text-blue-600 text-[12px] whitespace-nowrap">
            <p className="ml-2">{formatPhoneNumber(value)}</p>
          </span>
        ),
      },

      {
        Header: "Locations",
        accessor: "location",
        id: "location",
        width: 140,
        minWidth: 120,
        maxWidth: 160,
        disableSortBy: false,
        sortType: (rowA, rowB) => {
          const locA = normalizeLabel(
            rowA.original?.personalInfo?.addressInfo?.district
          );
          const locB = normalizeLabel(
            rowB.original?.personalInfo?.addressInfo?.district
          );

          if (!locA && locB) return 1;
          if (locA && !locB) return -1;

          return locA.localeCompare(locB);
        },
        headerStyle: {
          textAlign: "center",
          padding: "8px",
          paddingLeft: "12px",
        },
        style: {
          textAlign: "center",
          padding: "8px",
          paddingLeft: "12px",
        },
        Cell: ({ row: { original } }) => (
          <span className="text-[12px]">
            <p className="ml-8">
              {original?.personalInfo?.addressInfo?.district ? (
                normalizeLabel(original.personalInfo.addressInfo.district)
              ) : (
                <p className="">Unknown</p>
              )}
            </p>
          </span>
        ),
      },

      {
        Header: "Completion",
        accessor: "profileCompletion",
        id: "profileCompletion",
        width: 100,
        minWidth: 90,
        maxWidth: 110,
        disableSortBy: false,
        sortType: (rowA, rowB) => {
          const compA = rowA.original?.profileCompletion || "0 of 0";
          const compB = rowB.original?.profileCompletion || "0 of 0";
          const numA = parseInt(compA.split(" ")[0]) || 0;
          const numB = parseInt(compB.split(" ")[0]) || 0;
          return numA - numB;
        },
        headerStyle: { textAlign: "center", padding: "8px" },
        style: { textAlign: "center", padding: "8px" },
        Cell: ({ row: { original } }) => {
          const value = original?.profileCompletion;
          const completed = isProfileCompleted(original);

          return (
            <span
              className={`text-[12px] ${completed ? "text-green-500" : "text-red-500"
                }`}
            >
              <p className="w-40 ml-12">{value}</p>
            </span>
          );
        },
      },

      {
        Header: "Registered On",
        accessor: "registeredOn",
        id: "registeredOn",
        width: 125,
        minWidth: 120,
        maxWidth: 140,
        disableSortBy: false,
        sortType: (rowA, rowB) => {
          const dateA = new Date(rowA.original?.createdAt || 0).getTime();
          const dateB = new Date(rowB.original?.createdAt || 0).getTime();
          return dateA - dateB;
        },
        headerStyle: { textAlign: "center", padding: "8px" },
        style: { textAlign: "center", padding: "8px" },
        Cell: ({ row: { original } }) => (
          <span className="text-[12px] whitespace-nowrap">
            <p className="ml-10">
              {moment(original?.createdAt).format("DD-MM-YYYY")}
            </p>
          </span>
        ),
      },

      {
        Header: "Payment",
        accessor: "payment",
        id: "payment",
        width: 90,
        minWidth: 80,
        maxWidth: 100,
        disableSortBy: false,
        sortType: (rowA, rowB) => {
          const isStaffA =
            rowA.original?.role === "nurse" ||
            rowA.original?.role === "physiotherapist";
          const isStaffB =
            rowB.original?.role === "nurse" ||
            rowB.original?.role === "physiotherapist";

          if (!isStaffA && isStaffB) return 1;
          if (isStaffA && !isStaffB) return -1;
          if (!isStaffA && !isStaffB) return 0;

          const paidA = rowA.original?.paymentInfo?.registration?.paid ? 1 : 0;
          const paidB = rowB.original?.paymentInfo?.registration?.paid ? 1 : 0;

          return paidB - paidA;
        },
        headerStyle: { textAlign: "center", padding: "4px" },
        style: { textAlign: "center", padding: "4px" },
        Cell: ({ row: { original } }) => {
          const isStaff =
            original?.role === "nurse" || original?.role === "physiotherapist";
          if (!isStaff)
            return (
              <span className="text-[12px]">
                <p className="ml-10">-</p>
              </span>
            );
          return (
            <span
              className={`text-[12px] ${original?.paymentInfo?.registration?.paid
                  ? "text-green-600"
                  : "text-orange-400"
                }`}
            >
              <p className="w-40 ml-8">
                {original?.paymentInfo?.registration?.paid
                  ? "Paid"
                  : "Not Paid"}
              </p>
            </span>
          );
        },
      },

      {
        Header: "Aadhaar",
        id: "aadhaar",
        width: 95,
        minWidth: 90,
        maxWidth: 110,
        disableSortBy: false,

        headerStyle: {
          textAlign: "center",
          padding: "8px",
          display: "flex",
          justifyContent: "center",
        },

        style: {
          textAlign: "center",
          padding: "8px",
          display: "flex",
          justifyContent: "center",
        },

        accessor: (row) => {
          if (String(row?.role || "").toLowerCase() === "patient") return "na";
          return row?.personalInfo?.uploads?.aadhaar?.verified || "none";
        },

        sortType: (rowA, rowB) => {
          const isPatientA =
            String(rowA.original?.role || "").toLowerCase() === "patient";
          const isPatientB =
            String(rowB.original?.role || "").toLowerCase() === "patient";

          const statusA = isPatientA
            ? "na"
            : rowA.original?.personalInfo?.uploads?.aadhaar?.verified || "none";
          const statusB = isPatientB
            ? "na"
            : rowB.original?.personalInfo?.uploads?.aadhaar?.verified || "none";

          const order = {
            accepted: 1,
            pending: 2,
            rejected: 3,
            none: 4,
            na: 5,
          };
          return (order[statusA] ?? 99) - (order[statusB] ?? 99);
        },

        Cell: ({ row: { original } }) => {
          if (String(original?.role || "").toLowerCase() === "patient") {
            return (
              <div className="flex items-center justify-center w-full ml-4">
                <span className="text-[12px] text-gray-500 font-poppins">
                  N/A
                </span>
              </div>
            );
          }

          const uploads = original?.personalInfo?.uploads?.aadhaar;
          const status = uploads?.verified;
          const hasKey = uploads?.key;

          return (
            <div className="flex items-center justify-center w-full ml-4">
              {status === "accepted" && (
                <SiTicktick color="#00AA44" size={14} />
              )}
              {status === "pending" && hasKey && (
                <AiOutlineExclamationCircle color="orange" size={14} />
              )}
              {status === "pending" && !hasKey && <p>-</p>}
              {status === "rejected" && <MdCancel color="#FF0000" size={14} />}
              {!status && <p>-</p>}
            </div>
          );
        },
      },

      {
        Header: "Certificate",
        id: "certificate",
        width: 95,
        minWidth: 90,
        maxWidth: 110,
        disableSortBy: false,
        accessor: (row) => {
          const isHospitalOnboarded =
            row?.onboardedBy?.partnerType &&
            row.onboardedBy.partnerType !== "self";
          if (isHospitalOnboarded) return "na";

          const isStaff =
            row?.role === "nurse" || row?.role === "physiotherapist";
          if (!isStaff) return "none";
          const certificates = row?.educationalInfo || [];
          if (certificates.length === 0) return "none";

          const allAccepted = certificates.every(
            (c) => c?.certificate?.verified === "accepted"
          );
          if (allAccepted) return "accepted";
          const hasRejected = certificates.some(
            (c) => c?.certificate?.verified === "rejected"
          );
          if (hasRejected) return "rejected";
          return "pending";
        },
        sortType: (rowA, rowB) => {
          const statusA = rowA.values.certificate || "none";
          const statusB = rowB.values.certificate || "none";
          const order = {
            accepted: 1,
            pending: 2,
            rejected: 3,
            na: 4,
            none: 5,
          };
          return order[statusA] - order[statusB];
        },
        headerStyle: { textAlign: "center", padding: "8px" },
        style: { textAlign: "center", padding: "8px" },
        Cell: ({ row: { original } }) => {
          const isHospitalOnboarded =
            original?.onboardedBy?.partnerType &&
            original.onboardedBy.partnerType !== "self";

          if (isHospitalOnboarded) {
            return (
              <div className="flex items-center justify-center">
                <span className="text-[12px] text-gray-500">N/A</span>
              </div>
            );
          }

          const isStaff =
            original?.role === "nurse" || original?.role === "physiotherapist";
          if (!isStaff)
            return (
              <div className="flex items-center justify-center">
                <span>-</span>
              </div>
            );

          const certificates = original?.educationalInfo || [];
          if (certificates.length === 0)
            return (
              <div className="flex items-center justify-center">
                <span>-</span>
              </div>
            );

          const allAccepted = certificates.every(
            (c) => c?.certificate?.verified === "accepted"
          );
          const hasRejected = certificates.some(
            (c) => c?.certificate?.verified === "rejected"
          );
          const hasPending = certificates.some(
            (c) => c?.certificate?.verified === "pending"
          );

          if (allAccepted)
            return (
              <div className="flex items-center justify-center">
                <SiTicktick color="#00AA44" size={14} />
              </div>
            );
          if (hasRejected)
            return (
              <div className="flex items-center justify-center">
                <MdCancel color="#FF0000" size={14} />
              </div>
            );
          if (hasPending)
            return (
              <div className="flex items-center justify-center">
                <AiOutlineExclamationCircle color="orange" size={14} />
              </div>
            );
          return (
            <div className="flex items-center justify-center">
              <span>-</span>
            </div>
          );
        },
      },

      {
        Header: "License",
        id: "license",
        width: 80,
        minWidth: 70,
        maxWidth: 90,
        disableSortBy: false,
        accessor: (row) => {
          const isHospitalOnboarded =
            row?.onboardedBy?.partnerType &&
            row.onboardedBy.partnerType !== "self";
          if (isHospitalOnboarded) return "na";

          if (row?.subRole === "nursing-assistant") return "na";
          const requires =
            row?.role === "nurse" || row?.role === "physiotherapist";
          if (!requires) return "none";
          return row?.professionalInfo?.licenseInfo?.status || "none";
        },
        sortType: (rowA, rowB) => {
          const statusA = rowA.values.license || "none";
          const statusB = rowB.values.license || "none";
          const order = {
            accepted: 1,
            pending: 2,
            rejected: 3,
            na: 4,
            none: 5,
          };
          return order[statusA] - order[statusB];
        },
        headerStyle: { textAlign: "center", padding: "8px" },
        style: { textAlign: "center", padding: "8px" },
        Cell: ({ row: { original } }) => {
          const isHospitalOnboarded =
            original?.onboardedBy?.partnerType &&
            original.onboardedBy.partnerType !== "self";

          if (isHospitalOnboarded) {
            return (
              <div className="flex items-center justify-center">
                <span className="text-[12px] text-gray-500">N/A</span>
              </div>
            );
          }

          if (original?.subRole === "nursing-assistant")
            return (
              <div className="flex items-center justify-center">
                <span className="text-[12px]">N/A</span>
              </div>
            );
          const requires =
            original?.role === "nurse" || original?.role === "physiotherapist";
          if (!requires)
            return (
              <div className="flex items-center justify-center">
                <span>-</span>
              </div>
            );

          const status = original?.professionalInfo?.licenseInfo?.status;

          if (status === "accepted")
            return (
              <div className="flex items-center justify-center">
                <SiTicktick color="#00AA44" size={14} />
              </div>
            );
          if (status === "rejected")
            return (
              <div className="flex items-center justify-center">
                <MdCancel color="#FF0000" size={14} />
              </div>
            );
          if (status === "pending")
            return (
              <div className="flex items-center justify-center">
                <AiOutlineExclamationCircle color="orange" size={14} />
              </div>
            );
          return (
            <div className="flex items-center justify-center">
              <span>-</span>
            </div>
          );
        },
      },

      {
        Header: "Profile",
        id: "profile",
        width: 80,
        minWidth: 70,
        maxWidth: 90,
        disableSortBy: true,
        headerStyle: { textAlign: "start", padding: "8px" },
        style: { textAlign: "start", padding: "8px" },
        Cell: ({ row: { index, original } }) => (
          <button
            onClick={() => navigateToProfile(index, original)}
            className="inline-flex items-center justify-center hover:opacity-70 transition-opacity -ml-2"
            aria-label="View Profile"
          >
            <VscEye size={18} color="blue" />
          </button>
        ),
      },
    ];

    return baseColumns;
  };

  const isWithinThreeMonths = (dateString) => {
    if (!dateString) return false;
    const lastLogin = moment(dateString);
    const threeMonthsAgo = moment().subtract(3, "months");
    return lastLogin.isSameOrAfter(threeMonthsAgo);
  };

  const filteredData = useMemo(() => {
    let filtered = [...userList];

    if (verification === "pending" && fromDashboard) {
      filtered = filtered.filter((item) => {
        const isStaff =
          item?.role === "nurse" || item?.role === "physiotherapist";
        if (!isStaff) return false;

        const isNursingAssistant = item?.subRole === "nursing-assistant";
        const certificates = item?.educationalInfo || [];
        const hasCertPending =
          certificates.length > 0 &&
          certificates.some((cert) => cert?.certificate?.verified === "pending");
        const licenseStatus =
          item?.professionalInfo?.licenseInfo?.status || "";
        const hasLicensePending = licenseStatus === "pending";

        if (isNursingAssistant) return hasCertPending;
        return hasCertPending || hasLicensePending;
      });
    }

    // Role filter
    if (filters?.role?.length > 0) {
      filtered = filtered.filter((item) => {
        const userRole = item.role;
        const userSubRole = item.subRole;

        if (userRole === "nurse") {
          if (
            filters.role.includes("nurse") &&
            filters.role.includes("nursing-assistant")
          )
            return true;
          if (
            filters.role.includes("nursing-assistant") &&
            !filters.role.includes("nurse")
          )
            return userSubRole === "nursing-assistant";
          if (
            filters.role.includes("nurse") &&
            !filters.role.includes("nursing-assistant")
          )
            return !userSubRole || userSubRole !== "nursing-assistant";
        }

        if (filters.role.includes("patient") && userRole === "patient")
          return true;
        if (
          filters.role.includes("physiotherapist") &&
          userRole === "physiotherapist"
        )
          return true;
        return false;
      });
    }

    // Onboarded Type filter
    if (filters?.onboardedType?.length > 0) {
      filtered = filtered.filter((item) => {
        const isPartner =
          item?.onboardedBy?.partnerType &&
          item.onboardedBy.partnerType !== "self";
        const typeKey = isPartner ? "partner" : "individual";
        return filters.onboardedType.includes(typeKey);
      });
    }

    // -------------------- NEW: Partner Name filter --------------------
    if (filters?.partnerName?.length > 0) {
      const selected = new Set(filters.partnerName); // normalized keys
      filtered = filtered.filter((item) => {
        const pType = item?.onboardedBy?.partnerType;
        if (!pType || pType === "self") return false;
        const key = normalizePartnerName(item?.onboardedBy?.partnerName);
        return selected.has(key);
      });
    }

    // User Type filter
    if (filters?.userType?.length > 0) {
      filtered = filtered.filter((item) => {
        const lastLoggedIn = item?.sessionInfo?.lastLoggedIn;
        const isActive = isWithinThreeMonths(lastLoggedIn);
        if (filters.userType.includes("Active Users") && isActive) return true;
        if (filters.userType.includes("Dormant Users") && !isActive)
          return true;
        return false;
      });
    }

    // Profile filter
    if (filters?.profile?.length > 0) {
      filtered = filtered.filter((item) => {
        const isCompleted = isProfileCompleted(item);
        if (filters.profile.includes("completed") && isCompleted) return true;
        if (filters.profile.includes("incompleted") && !isCompleted) return true;
        return false;
      });
    }

    // Registered On filter
    if (filters?.registeredOn?.length > 0) {
      if (filters.registeredOn.includes("latest")) {
        filtered = [...filtered].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      } else if (filters.registeredOn.includes("earliest")) {
        filtered = [...filtered].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
      }
    }

    // Payment filter
    if (filters?.payment?.length > 0) {
      filtered = filtered.filter((item) => {
        const paidStatus = item?.paymentInfo?.registration?.paid;
        const statusLabel = paidStatus ? "paid" : "unpaid";
        return filters.payment.includes(statusLabel);
      });
    }

    // Aadhaar filter
    if (filters?.aadhar?.length > 0) {
      filtered = filtered.filter((item) => {
        const status = item?.personalInfo?.uploads?.aadhaar?.verified;
        return filters.aadhar.includes(status);
      });
    }

    // Certificate filter
    if (filters?.certificate?.length > 0) {
      filtered = filtered.filter((item) => {
        const isHospitalOnboarded =
          item?.onboardedBy?.partnerType &&
          item.onboardedBy.partnerType !== "self";
        if (isHospitalOnboarded) return false;

        const isStaff =
          item?.role === "nurse" || item?.role === "physiotherapist";
        if (!isStaff) return false;

        const certificates = item?.educationalInfo || [];
        if (certificates.length === 0) return false;

        const allAccepted =
          certificates.length > 0 &&
          certificates.every((cert) => cert?.certificate?.verified === "accepted");
        const hasRejected = certificates.some(
          (cert) => cert?.certificate?.verified === "rejected"
        );
        const hasPending = certificates.some(
          (cert) => cert?.certificate?.verified === "pending"
        );

        if (filters.certificate.includes("accepted") && allAccepted) return true;
        if (filters.certificate.includes("rejected") && hasRejected) return true;
        if (filters.certificate.includes("pending") && hasPending) return true;
        return false;
      });
    }

    // Location filter
    if (filters?.location?.length > 0) {
      const selected = new Set(filters.location || []);
      const selectedDistricts = new Set(
        Array.from(selected).filter((v) => !v.includes("::"))
      );
      const selectedCityPairs = Array.from(selected)
        .filter((v) => v.includes("::"))
        .map((v) => {
          const [d, c] = v.split("::");
          return { district: d, city: c };
        });

      filtered = filtered.filter((item) => {
        const district = normalizeLabel(item?.personalInfo?.addressInfo?.district);
        const city = normalizeLabel(
          item?.personalInfo?.addressInfo?.city ||
          item?.personalInfo?.addressInfo?.area
        );

        if (selectedDistricts.has(district)) return true;

        if (district && city) {
          return selectedCityPairs.some(
            (p) => p.district === district && p.city === city
          );
        }

        return false;
      });
    }

    // License filter
    if (filters?.license?.length > 0) {
      filtered = filtered.filter((item) => {
        const isHospitalOnboarded =
          item?.onboardedBy?.partnerType &&
          item.onboardedBy.partnerType !== "self";
        if (isHospitalOnboarded) return false;

        const isStaff =
          item?.role === "nurse" || item?.role === "physiotherapist";
        if (!isStaff) return false;

        const isNursingAssistant = item?.subRole === "nursing-assistant";
        if (isNursingAssistant) return false;

        const status = item?.professionalInfo?.licenseInfo?.status || "";
        return filters.license.includes(status);
      });
    }

    // Search term filter
    if (searchTerm) {
      filtered = filtered.filter((item) => {
        const phone = String(item.phone || "");
        const name =
          item?.personalInfo?.basicInfo?.nameInfo?.fullName?.toLowerCase() || "";
        const district = normalizeLabel(
          item?.personalInfo?.addressInfo?.district
        ).toLowerCase();

        return (
          name.includes(searchTerm.toLowerCase()) ||
          phone.includes(searchTerm) ||
          district.includes(searchTerm.toLowerCase())
        );
      });
    }

    if (filters?.registeredOn?.length > 0) {
      return filtered;
    }

    return filtered.slice().reverse();
  }, [filters, userList, searchTerm, verification, fromDashboard]);

  useEffect(() => {
    setNoUsersFound(filteredData.length === 0);
  }, [filteredData]);

  const columns = useMemo(() => getColumns(), [selectedValue]); // keeping your logic

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page: tablePage,
    prepareRow,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state: { pageIndex, sortBy: currentSortBy },
    rows,
  } = useTable(
    {
      columns,
      data: filteredData.length > 0 ? filteredData : userList,
      initialState: {
        pageSize: selectedPageCount,
        pageIndex: paginationPage,
        sortBy: sortBy.length > 0 ? sortBy : [],
      },
    },
    useSortBy,
    usePagination
  );

  useEffect(() => {
    localStorage.setItem("sortBy", JSON.stringify(currentSortBy));
  }, [currentSortBy]);

  useEffect(() => {
    if (pageIndex !== paginationPage) {
      setPaginationPage(pageIndex);
    }
  }, [pageIndex, paginationPage]);

  const convertUserToCSVRow = useCallback(
    (u) => {
      const isPatient = u?.role === "patient";
      const piName = u?.personalInfo?.basicInfo?.nameInfo;
      const cpName = u?.contactPersonInfo?.basicInfo?.nameInfo;
      const title =
        (isPatient ? piName?.title || cpName?.title : piName?.title) || "";
      const fullName =
        (isPatient ? piName?.fullName || cpName?.fullName : piName?.fullName) ||
        "";
      const Name = `${title ? title + ". " : ""}${fullName}`.trim();

      let Role = "";
      if (u?.role === "physiotherapist") Role = "Physio";
      else if (u?.subRole === "nursing-assistant") Role = "Nurse-Assist";
      else if (u?.subRole && u?.role === "nurse")
        Role = capitalizeFirstLetter(u.subRole);
      else Role = capitalizeFirstLetter(u?.role || "");

      const Phone = formatPhoneNumber(u?.phone || "");

      const Location = u?.personalInfo?.addressInfo?.district
        ? normalizeLabel(u.personalInfo.addressInfo.district)
        : "Undefined";

      const Completion = u?.profileCompletion || "0 of 0";

      const RegisteredOn = u?.createdAt
        ? moment(u.createdAt).format("DD-MM-YYYY")
        : "";

      const isStaff = u?.role === "nurse" || u?.role === "physiotherapist";
      const isHospitalOnboarded =
        u?.onboardedBy?.partnerType && u.onboardedBy.partnerType !== "self";

      const Payment = isStaff
        ? u?.paymentInfo?.registration?.paid
          ? "Paid"
          : "Not Paid"
        : "-";

      const aadhaarStatus = u?.personalInfo?.uploads?.aadhaar?.verified;
      const Aadhaar = aadhaarStatus ? capitalizeFirstLetter(aadhaarStatus) : "-";

      let Certificate = "-";
      if (isHospitalOnboarded) {
        Certificate = "N/A";
      } else if (isStaff) {
        const certs = u?.educationalInfo || [];
        if (certs.length > 0) {
          const allAccepted = certs.every(
            (c) => c?.certificate?.verified === "accepted"
          );
          const hasRejected = certs.some(
            (c) => c?.certificate?.verified === "rejected"
          );
          const hasPending = certs.some(
            (c) => c?.certificate?.verified === "pending"
          );
          Certificate = allAccepted
            ? "Accepted"
            : hasRejected
              ? "Rejected"
              : hasPending
                ? "Pending"
                : "-";
        }
      }

      let License = "-";
      if (isHospitalOnboarded) {
        License = "N/A";
      } else if (u?.subRole === "nursing-assistant") {
        License = "N/A";
      } else if (isStaff) {
        const lic = u?.professionalInfo?.licenseInfo?.status;
        License = lic ? capitalizeFirstLetter(lic) : "-";
      }

      return {
        Name,
        Role,
        Phone,
        Location,
        Completion,
        RegisteredOn,
        Payment,
        Aadhaar,
        Certificate,
        License,
      };
    },
    [normalizeLabel]
  );

  const handleExport = useCallback(() => {
    let exportType = "All Users";
    let isSorted = currentSortBy && currentSortBy.length > 0;
    let isFiltered = isFilterApplied || searchTerm;

    if (isSorted && isFiltered) {
      exportType = "Filtered & Sorted Data";
    } else if (isSorted) {
      exportType = "Sorted Data";
    } else if (isFiltered) {
      exportType = "Filtered Data";
    }

    const sortInfo = isSorted
      ? ` (Sorted by: ${currentSortBy
        .map((s) => `${s.id} ${s.desc ? "DESC" : "ASC"}`)
        .join(", ")})`
      : "";

    Modal.confirm({
      title: "Confirm Export",
      content: (
        <div>
          <p>Are you sure you want to export the user data to CSV?</p>
          <p className="mt-2 text-sm text-gray-600">
            <strong>Export Type:</strong> {exportType}
            {sortInfo}
          </p>
          <p className="text-sm text-gray-600">
            <strong>Total Records:</strong> {rows.length}
          </p>
        </div>
      ),
      okText: "Export",
      cancelText: "Cancel",
      onOk: () => {
        try {
          const dataset = rows.map((row) => row.original);

          if (dataset.length === 0) {
            message.warning("No data to export");
            return;
          }

          const csvRows = dataset.map((u) => convertUserToCSVRow(u));

          const fields = [
            "Name",
            "Role",
            "Phone",
            "Location",
            "Completion",
            "RegisteredOn",
            "Payment",
            "Aadhaar",
            "Certificate",
            "License",
          ];

          const data = csvRows.map((r) => fields.map((f) => r[f] ?? ""));
          const csv = Papa.unparse({ fields, data });

          const blob = new Blob(["\ufeff", csv], {
            type: "text/csv;charset=utf-8;",
          });

          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;

          let filename = "users";
          if (isFiltered) filename += "_filtered";
          if (isSorted) filename += "_sorted";
          filename += `_${moment().format("YYYYMMDD_HHmmss")}.csv`;

          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          message.success(
            `Exported ${csvRows.length} users to CSV (${exportType})`
          );
        } catch (e) {
          console.error("CSV export failed", e);
          message.error("Export failed. Please try again.");
        }
      },
    });
  }, [
    rows,
    currentSortBy,
    isFilterApplied,
    searchTerm,
    convertUserToCSVRow,
  ]);

  return (
    <>
      {loading ? (
        <div className="">
          <Loader loading={loading} />
        </div>
      ) : (
        <div className="bg-[#F3F6F5]">
          <div className="overflow-x-auto bg-white rounded-2xl shadow-lg h-[89vh] ml-2 flex flex-col gap-3">
            <div className="flex justify-between items-center mt-[-20px]">
              <div className="flex flex-row items-center mt-8 p-2">
                <p className="text-[14px] flex item-center align-center ml-2 font-bold">
                  {(() => {
                    const count =
                      filteredData.length > 0
                        ? filteredData.length
                        : userList.length;
                    const listType =
                      selectedValue === "nurse"
                        ? "Nurse"
                        : selectedValue === "patient"
                          ? "Patient"
                          : "User";
                    return `${listType} List (${count})`;
                  })()}
                </p>
              </div>

              <div className="flex flex-row items-center mr-4 mt-6 p-1">
                {/* Page size selector */}
                <div
                  className="mr-4 cursor-pointer"
                  title="Set the page user count"
                >
                  <div className="w-[80px]">
                    <CommonDropdown
                      options={pageCount}
                      value={selectedPageCount}
                      onChange={handleChangePageCount}
                      page={"user"}
                    />
                  </div>
                </div>

                {/* Filters button */}
                <div className="relative w-[220px] max-w-xs mr-3">
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="w-full border border-blue-600 rounded-md text-[12px] text-blue-700 px-2 py-0.5 flex justify-between items-center gap-2"
                  >
                    <div className="flex items-center gap-2">
                      <MdOutlineFilterList className="h-5 w-5" />
                      Filters
                      {isFilterApplied && (
                        <span className="bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                          {Object.values(filters).flat().length}
                        </span>
                      )}
                    </div>

                    {dropdownOpen ? (
                      <MdKeyboardArrowUp className="h-5 w-5" />
                    ) : (
                      <MdKeyboardArrowDown className="h-5 w-5" />
                    )}
                  </button>

                  {dropdownOpen && (
                    <div className="absolute mt-2 w-full bg-white border border-blue-400 rounded-md shadow-lg z-10 p-4 space-y-4 max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-[#4951EC] scrollbar-track-gray-200">
                      {/* All filters EXCEPT Location */}
                      {[
                        {
                          label: "Role",
                          key: "role",
                          options: [
                            "nurse",
                            "nursing-assistant",
                            "physiotherapist",
                            "patient",
                          ],
                        },
                        {
                          label: "User Type",
                          key: "userType",
                          options: ["Active Users", "Dormant Users"],
                        },
                        {
                          label: "Onboarded Type",
                          key: "onboardedType",
                          options: ["individual", "partner"],
                        },
                        {
                          label: "Profile",
                          key: "profile",
                          options: ["completed", "incompleted"],
                        },
                        {
                          label: "Registered On",
                          key: "registeredOn",
                          options: ["latest", "earliest"],
                        },
                        {
                          label: "Payment",
                          key: "payment",
                          options: ["paid", "unpaid"],
                        },
                        {
                          label: "Aadhar",
                          key: "aadhar",
                          options: ["pending", "accepted", "rejected"],
                        },
                        {
                          label: "Certificate",
                          key: "certificate",
                          options: ["pending", "accepted", "rejected"],
                        },
                        {
                          label: "License",
                          key: "license",
                          options: ["pending", "accepted", "rejected"],
                        },
                      ].map(({ label, key, options }) => (
                        <div key={key}>
                          <p className="text-[12px] font-semibold text-blue-500 mb-1">
                            {label}
                          </p>
                          {options.map((option) => (
                            <label
                              key={option}
                              className="flex items-center space-x-2 text-[12px] text-gray-700 mb-1"
                            >
                              <input
                                type="checkbox"
                                checked={(filters[key] || []).includes(option)}
                                onChange={() => {
                                  setFilters((prev) => {
                                    const current = prev[key] || [];
                                    const alreadySelected =
                                      current.includes(option);
                                    return {
                                      ...prev,
                                      [key]: alreadySelected
                                        ? current.filter((val) => val !== option)
                                        : [...current, option],
                                    };
                                  });
                                }}
                              />
                              <span>
                                {key === "onboardedType"
                                  ? option === "individual"
                                    ? "Individual (Self-Onboarded)"
                                    : "Partner"
                                  : capitalizeFirstLetter(option)}
                              </span>
                            </label>
                          ))}
                        </div>
                      ))}

                      {/* -------------------- NEW: Partner Name filter -------------------- */}
                      {partnerNameOptions.length > 0 && (
                        <div>
                          <p className="text-[12px] font-semibold text-blue-500 mb-1">
                            Partner Name
                          </p>

                          {partnerNameOptions.map((p) => (
                            <label
                              key={p.value}
                              className="flex items-center space-x-2 text-[12px] text-gray-700 mb-1"
                            >
                              <input
                                type="checkbox"
                                checked={(filters.partnerName || []).includes(
                                  p.value
                                )}
                                onChange={() => togglePartnerName(p.value)}
                              />
                              <span>
                                {p.label} ({p.count})
                              </span>
                            </label>
                          ))}
                        </div>
                      )}

                      {/* Location (Nested: District -> Cities) */}
                      <div>
                        <p className="text-[12px] font-semibold text-blue-500 mb-1">
                          Locations
                        </p>

                        {Object.entries(locationMap).map(
                          ([district, cities]) => {
                            const districtChecked = (
                              filters.location || []
                            ).includes(district);
                            const expanded =
                              expandedDistricts[district] || false;

                            const districtCount = getDistrictCount(district);

                            return (
                              <div key={district} className="mb-1">
                                <div className="flex items-center justify-between">
                                  <label className="flex items-center space-x-2 text-[12px] text-gray-700">
                                    <input
                                      type="checkbox"
                                      checked={districtChecked}
                                      onChange={() =>
                                        toggleLocationDistrict(district)
                                      }
                                    />
                                    <span>
                                      {district} ({districtCount})
                                    </span>
                                  </label>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      toggleDistrictExpand(district)
                                    }
                                    className="text-blue-600"
                                  >
                                    {expanded ? (
                                      <MdKeyboardArrowUp className="h-5 w-5" />
                                    ) : (
                                      <MdKeyboardArrowDown className="h-5 w-5" />
                                    )}
                                  </button>
                                </div>

                                {/* Cities */}
                                {expanded && (
                                  <div className="ml-6 mt-1 space-y-1">
                                    {cities.map((city) => {
                                      const cityChecked = (
                                        filters.location || []
                                      ).includes(`${district}::${city}`);
                                      const cityCount = getCityCount(
                                        district,
                                        city
                                      );

                                      return (
                                        <label
                                          key={city}
                                          className="flex items-center space-x-2 text-[12px]"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={cityChecked}
                                            onChange={() =>
                                              toggleLocationCity(
                                                district,
                                                city
                                              )
                                            }
                                          />
                                          <span>
                                            {city} ({cityCount})
                                          </span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Reset filters */}
                <div
                  onClick={() => {
                    if (!isFilterApplied) return;

                    setFilters({
                      role: [],
                      userType: [],
                      profile: [],
                      registeredOn: [],
                      payment: [],
                      aadhar: [],
                      certificate: [],
                      license: [],
                      location: [],
                      onboardedType: [],
                      partnerName: [], // NEW reset
                    });

                    setExpandedDistricts({});
                    setDropdownOpen(false);
                    setShowResetPopup(true);
                    setTimeout(() => setShowResetPopup(false), 1500);
                    navigate("/user", { replace: true, state: {} });
                  }}
                  className={`mr-4 ${isFilterApplied ? "cursor-pointer" : "cursor-not-allowed"
                    }`}
                  title="Reset filter"
                >
                  <GrPowerReset
                    color={isFilterApplied ? "blue" : "gray"}
                    size={18}
                    style={{ opacity: isFilterApplied ? 0.8 : 0.3 }}
                  />
                </div>

                {/* Export button */}
                <Space size="small">
                  <Button
                    size="small"
                    onClick={handleExport}
                    title={`Export ${rows.length} records${currentSortBy?.length > 0 ? " (sorted)" : ""
                      }${isFilterApplied ? " (filtered)" : ""}`}
                  >
                    <HiOutlineDownload color={"blue"} size={18} />
                  </Button>
                </Space>

                {showResetPopup && (
                  <div className="absolute top-8 left-1/2 -translate-x-1/2 bg-white text-gray-800 text-sm rounded-md px-3 py-1 shadow-md border border-gray-200 z-50">
                    Filters have been reset
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto max-h-[75vh] mt-[-15px]">
              {noUsersFound ? (
                <div className="flex flex-col items-center justify-center h-[60vh]">
                  <p className="text-gray-500 text-lg">No users found</p>
                  <p className="text-gray-400 text-sm mt-2">
                    Try adjusting your filters or search term
                  </p>
                </div>
              ) : (
                <ReusableTable
                  columns={getColumns()}
                  getTableProps={getTableProps}
                  getTableBodyProps={getTableBodyProps}
                  headerGroups={headerGroups}
                  tablePage={tablePage}
                  prepareRow={prepareRow}
                  filterIcon={CommonImages.filter}
                  searchTerm={searchTerm}
                  drop={dropdownOpen}
                />
              )}
            </div>

            {!noUsersFound && (
              <div className="mb-2">
                <Page
                  paginationPage={paginationPage}
                  nextPage={nextPage}
                  previousPage={previousPage}
                  canPreviousPage={canPreviousPage}
                  canNextPage={canNextPage}
                  pageOptions={pageOptions}
                  screen="userList"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default User;